import { useEffect } from 'react';
import type { RefObject } from 'react';
import {
  getClearSelection,
  getConnectedEdges,
  getNodeIdsInside,
  getSelectionChanges,
  pointToFlow,
  rectFromCorners,
} from '../core';
import type { XYPosition } from '../core';
import { useStoreApi } from '../store';
import type { MindmapStoreApi } from '../store';
import { isInsideClass, rendererPoint, suppressNextClick } from '../utils';

const PRIMARY_BUTTON = 0;
const PANE_CLASS = 'cm-pane';

/** Selects exactly the nodes fully inside the box and the edges connected to them; returns the node ids. */
const selectInside = (store: MindmapStoreApi, from: XYPosition, to: XYPosition, previous: string): string => {
  const { nodes, edges, nodeLookup, viewport, applySelection } = store.getState();
  const flowRect = rectFromCorners(pointToFlow(from, viewport), pointToFlow(to, viewport));
  const nodeIds = getNodeIdsInside(Array.from(nodeLookup.values()), flowRect);
  const key = nodeIds.join('|');
  if (key === previous) return key;
  const edgeIds = getConnectedEdges(nodes.filter((node) => nodeIds.includes(node.id)), edges).map((edge) => edge.id);
  applySelection({ nodeChanges: getSelectionChanges(nodes, nodeIds), edgeChanges: getSelectionChanges(edges, edgeIds) });
  return key;
};

/** Shift+drag on the pane draws a selection box (v11 `selectionKeyCode` "Shift", `SelectionMode.Full`). */
export const useBoxSelect = (rendererRef: RefObject<HTMLElement>): void => {
  const store = useStoreApi();
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return undefined;
    const doc = renderer.ownerDocument;
    let start: XYPosition | null = null;
    let selectedKey = '';

    const handlePointerMove = (event: PointerEvent) => {
      if (!start) return;
      const point = rendererPoint(renderer, event);
      store.getState().setSelectionRect(rectFromCorners(start, point));
      selectedKey = selectInside(store, start, point, selectedKey);
    };
    const handlePointerUp = () => {
      doc.removeEventListener('pointermove', handlePointerMove);
      doc.removeEventListener('pointerup', handlePointerUp);
      const hasMoved = store.getState().selectionRect !== null;
      store.getState().setSelectionRect(null);
      start = null;
      if (hasMoved && doc.defaultView) {
        suppressNextClick(doc.defaultView);
        return;
      }
      const { nodes, edges, applySelection } = store.getState();
      applySelection(getClearSelection(nodes, edges));
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== PRIMARY_BUTTON || !event.shiftKey || !isInsideClass(event.target, PANE_CLASS)) return;
      start = rendererPoint(renderer, event);
      selectedKey = '';
      doc.addEventListener('pointermove', handlePointerMove);
      doc.addEventListener('pointerup', handlePointerUp);
    };

    renderer.addEventListener('pointerdown', handlePointerDown);
    return () => {
      renderer.removeEventListener('pointerdown', handlePointerDown);
      doc.removeEventListener('pointermove', handlePointerMove);
      doc.removeEventListener('pointerup', handlePointerUp);
    };
  }, [rendererRef, store]);
};
